package dev.lingvo.ops.pages.users.userId

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.components.OpsCapNotice
import dev.lingvo.ops.components.OpsFact
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/users/{userId}")
@Composable
fun UserPage() {
    val id = rememberPageContext().route.params["userId"].orEmpty()
    OpsShell("Users") {
        val identity = rememberOpsResource(id, OpsApi::user)
        val workspaces = rememberOpsResource(id, OpsApi::userWorkspaces)
        val credentials = rememberOpsResource(id, OpsApi::credentials)
        val mints = rememberOpsResource(id, OpsApi::tokenMints)
        fun refresh() { identity.refresh(); workspaces.refresh(); credentials.refresh(); mints.refresh() }
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            Link("/users") { Text("Back to users") }
            OpsPageHeader("User", "Review the account, membership responsibilities and recorded access activity.",
                identity.state.isRefreshing || workspaces.state.isRefreshing || credentials.state.isRefreshing || mints.state.isRefreshing, ::refresh)
            OpsResourceStates(identity.state, "User not found", "This user has no accessible record.", retry = identity::refresh) { user ->
                OpsPanel("Identity") {
                    OpsFact("Name", user.name)
                    OpsFact("Email", user.email)
                    OpsFact("Email verified", if (user.verified) "Yes" else "No")
                    OpsFact("Password sign-in", if (user.hasPassword) "Available" else "Not set")
                    OpsFact("Account closure", formatUtc(user.closingAt, absent = "Not scheduled"))
                    OpsFact("Created", formatUtc(user.createdAt))
                }
            }
            OpsPanel("Workspaces", "A sole owner must resolve workspace ownership before account closure.") {
                OpsResourceStates(workspaces.state, "No workspace memberships", "No workspace membership was returned.", retry = workspaces::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "memberships")
                    window.rows.forEach { row ->
                        P { Link("/workspaces/${row.orgId}") { Text(row.name) }; Text(" · ${row.role.name} · ${if (row.soleOwner) "sole owner" else "not sole owner"}") }
                    }
                }
            }
            OpsPanel("Credentials", "Types and coarse activity only. No prefix, fragment, hash or IP is shown.") {
                OpsResourceStates(credentials.state, "No recorded credentials", "No credential rows were returned.", retry = credentials::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "credentials")
                    window.rows.forEach { row -> P { Text("${row.kind.name} · ${row.lastSeenBucket} · ${row.orgSlug ?: "No workspace"}") } }
                }
            }
            OpsPanel("Email delivery", "The console does not retrieve message delivery history.") {
                P { Text("Check Lingvo's recorded send attempts and the mail provider's operational view. An attempted send does not prove inbox delivery.") }
            }
            OpsPanel("Token mints") {
                OpsResourceStates(mints.state, "No token mint activity", "No token mint rows were returned.", retry = mints::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "token mints")
                    window.rows.forEach { row -> P { Text("${row.kind} · ${formatUtc(row.createdAt)} · ${if (row.consumedAt == null) "unconsumed" else "consumed"}") } }
                }
            }
        }
    }
}
